import React, { useEffect, useState } from 'react';
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Switch,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import {
  OrderStatus,
  TransactionType,
} from '@Encore-FastStartup-Phase-Out/encore-shared';

import { useStore } from '../../hooks/useStore';
import ListingTypePill from '../common/ListingTypePill';
import { parseDate } from '../../utils/date';
import ConfirmationModal from '../modals/ConfirmationModal';
import RefundModal from '../modals/RefundModal';
import {
  refundReasonToString,
  transactionTypeToString,
} from '../../utils/string';

import InfoDisplay from './InfoDisplay';
import Section from './Section';

const ListingDetails = () => {
  const {
    listingStore: { displayedSoldListing, cancelOrder },
    orderStore: {
      displayedListingOrder,
      getOrderWithTransactions,
      updateOrder,
      makeOrderPayout,
    },
  } = useStore();
  const parsedDate = parseDate(displayedSoldListing.createdAt);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isRefundModalOpen, setIsRefundModalOpen] = useState(false);
  const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);
  const [isCheckedOrderPayout, setIsCheckedOrderPayout] = useState(
    displayedListingOrder?.isAutomaticPayoutEnabled,
  );
  const [doesTransactionHavePayout, setDoesTransactionHavePayout] =
    useState(false);
  const toast = useToast();

  // replace http with https to avoid mixed content error
  const videoUrl = displayedSoldListing?.listing?.video.replace(
    'http://',
    'https://',
  );

  // Calculate payout amounts accounting for discount
  const discount = Number(displayedListingOrder?.discount ?? 0);
  const price = Number(displayedListingOrder?.price ?? 0);
  const effectivePrice = Math.max(0, price - discount);
  const sellerFee = Number(displayedListingOrder?.sellerFee ?? 0);
  const payoutAmount = (effectivePrice * (1 - sellerFee / 100)).toFixed(2);

  const handleCancelOrder = async () => {
    try {
      await cancelOrder(displayedSoldListing.id);
      toast({
        title: 'Success',
        description: 'Order cancelled successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (e: any) {
      console.error(e);
      const message = e.response?.data?.message || e.message;

      toast({
        title: 'Error',
        description: message || 'Failed to cancel order',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsCancelModalOpen(false);
    }
  };

  const handlePayoutOrder = async () => {
    await updateOrder({
      isAutomaticPayoutEnabled: !isCheckedOrderPayout,
    });
    setIsCheckedOrderPayout((prev) => !prev);
    await makeOrderPayout();
    await getOrderWithTransactions();
  };

  const handleCancelModalClose = () => {
    setIsCancelModalOpen(false);
  };

  const handleClickCancel = () => {
    setIsCancelModalOpen(true);
  };

  const handleRefundModalClose = () => {
    setIsRefundModalOpen(false);
  };

  const handleClickRefund = () => {
    setIsRefundModalOpen(true);
  };

  const handleClickPayout = () => {
    setIsPayoutModalOpen(true);
  };

  const handlePayoutModalClose = () => {
    setIsPayoutModalOpen(false);
  };

  const onSwitchChange = async () => {
    await updateOrder({
      isAutomaticPayoutEnabled: !isCheckedOrderPayout,
    });
    setIsCheckedOrderPayout((prev) => !prev);
  };

  useEffect(() => {
    if (displayedSoldListing) {
      (async () => {
        await getOrderWithTransactions();
      })();
    }
  }, [displayedSoldListing]);

  useEffect(() => {
    if (displayedListingOrder) {
      setIsCheckedOrderPayout(displayedListingOrder.isAutomaticPayoutEnabled);
      const transactionOrderPayout = displayedListingOrder.transactions?.find(
        (transaction) => transaction.type === TransactionType.OrderPayout,
      );
      if (transactionOrderPayout) {
        setDoesTransactionHavePayout(true);
      }
    }
  }, [displayedListingOrder]);

  return (
    <Box p={4}>
      <Box display="flex" gap={5} mb={5}>
        <AspectRatio ratio={1} flex={2}>
          <iframe title="video" src={videoUrl} allowFullScreen />
        </AspectRatio>
        <Text flex={1}>{displayedSoldListing?.listing?.title}</Text>
      </Box>
      <VStack spacing={4} align="start">
        <Section
          header="Shipping Information"
          content={
            <VStack align="start" spacing={1}>
              <InfoDisplay
                title={'Order Tracking No:'}
                value={displayedSoldListing?.trackingCode}
              />
              <InfoDisplay
                title={'Order Status:'}
                value={displayedSoldListing?.status}
              />
              <InfoDisplay
                title={'Shipping Price:'}
                value={'$' + displayedSoldListing?.shippingPrice}
              />
              <InfoDisplay
                title={'Order Price:'}
                value={'$' + displayedSoldListing?.price}
              />
              <InfoDisplay
                title={'Date Sold:'}
                value={parsedDate.date + ' ' + parsedDate.time}
              />
            </VStack>
          }
        />
        <Section
          header="Listing Detail"
          content={
            <VStack align="start" spacing={1}>
              <InfoDisplay
                title={'Listing id:'}
                value={displayedSoldListing.listing?.id}
              />
              <InfoDisplay
                title={'Listing Description:'}
                value={displayedSoldListing?.listing?.description}
              />
              <InfoDisplay
                title={'Listing Type:'}
                value={
                  <ListingTypePill
                    type={displayedSoldListing.listing?.listingType}
                  />
                }
              />
            </VStack>
          }
        />
        <Section
          header="Buyer Information"
          content={
            <VStack align="start" spacing={1}>
              <InfoDisplay
                title={'Buyer username:'}
                value={displayedSoldListing?.user?.userName}
              />
              <InfoDisplay
                title={'E-mail:'}
                value={displayedSoldListing?.user?.email}
              />
            </VStack>
          }
        />
        <Section
          header="Seller Information"
          content={
            <VStack align="start" spacing={1}>
              <InfoDisplay
                title={'Seller username:'}
                value={displayedSoldListing?.seller?.userName}
              />
              <InfoDisplay
                title={'E-mail:'}
                value={displayedSoldListing?.seller?.email}
              />
            </VStack>
          }
        />
        {displayedListingOrder?.transactions?.length ? (
          <Section
            header="Transaction History"
            content={
              <VStack align="start" spacing={2}>
                {displayedListingOrder.transactions.map((tx) => (
                  <Box
                    key={tx.id}
                    p={3}
                    borderWidth="1px"
                    borderRadius="md"
                    w="100%"
                  >
                    <Text>
                      <strong>Type:</strong> {transactionTypeToString(tx.type)}
                    </Text>
                    <Text>
                      <strong>Status:</strong> {tx.status}
                    </Text>
                    <Text>
                      <strong>Amount:</strong> ${Number(tx.amount).toFixed(2)}
                    </Text>
                    {tx.refundReason && (
                      <Text>
                        <strong>Reason:</strong>{' '}
                        {refundReasonToString(tx.refundReason)}
                      </Text>
                    )}
                    {tx.refundDate && (
                      <Text>
                        <strong>Refunded At:</strong>{' '}
                        {new Date(tx.refundDate).toLocaleString()}
                      </Text>
                    )}
                  </Box>
                ))}
              </VStack>
            }
          />
        ) : null}
        {displayedListingOrder?.status !== OrderStatus.SHIPPING_DELIVERED &&
          !doesTransactionHavePayout && (
            <FormControl
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              w="100%"
            >
              <HStack spacing={4} w="100%" justify="space-between">
                <FormLabel
                  htmlFor="auto-payout"
                  mb="0"
                  fontWeight="bold"
                  fontSize="x-large"
                >
                  Automatic Payout
                </FormLabel>

                <HStack>
                  <Switch
                    id="auto-payout"
                    isChecked={isCheckedOrderPayout}
                    onChange={onSwitchChange}
                    colorScheme={isCheckedOrderPayout ? 'green' : 'gray'}
                  />
                  <Badge
                    fontSize="small"
                    colorScheme={isCheckedOrderPayout ? 'green' : 'gray'}
                  >
                    {isCheckedOrderPayout ? 'Enabled' : 'Disabled'}
                  </Badge>
                </HStack>
              </HStack>
            </FormControl>
          )}

        <Button
          w={'100%'}
          py="25px"
          color="green.400"
          variant="contained"
          border="2px"
          borderColor="green.400"
          onClick={handleClickRefund}
        >
          Refund Order
        </Button>
        {!doesTransactionHavePayout && (
          <Button
            w={'100%'}
            py="25px"
            color="green.400"
            variant="contained"
            border="2px"
            borderColor="green.400"
            onClick={handleClickPayout}
          >
            Payout Order
          </Button>
        )}
        <Button
          w={'100%'}
          py="25px"
          color="red.400"
          variant="contained"
          border="2px"
          borderColor="red.400"
          onClick={handleClickCancel}
        >
          Cancel Order
        </Button>
        <ConfirmationModal
          isOpen={isCancelModalOpen}
          onClose={handleCancelModalClose}
          title={'Cancel order?'}
          subtitle={'This action cannot be undone'}
          cancelActionLabel={'Dismiss'}
          confirmActionLabel={'Confirm'}
          onCancel={handleCancelModalClose}
          onConfirm={handleCancelOrder}
        />
        <RefundModal
          isOpen={isRefundModalOpen}
          onClose={handleRefundModalClose}
        />
        <ConfirmationModal
          isOpen={isPayoutModalOpen}
          onClose={handlePayoutModalClose}
          title={'Payout Order Manually'}
          subtitle={`You are about to payout $${payoutAmount} (Price: $${price}${
            discount > 0 ? ` - Discount: $${discount.toFixed(2)}` : ''
          }${discount > 0 ? ` = $${effectivePrice.toFixed(2)}` : ''}, Seller Fee: ${sellerFee}%)`}
          cancelActionLabel={'Dismiss'}
          confirmActionLabel={'Confirm'}
          onCancel={handlePayoutModalClose}
          onConfirm={handlePayoutOrder}
        />
      </VStack>
    </Box>
  );
};

export default observer(ListingDetails);
